import { radiansToDegrees } from "@buildwithflux/shared";
import { parse as kicadParser } from "kicad-to-json";
import { getTerminalPositionModifiers } from "../../../../../../../imports/common/PartImportHelpers";
import { DrawRecordTypes, PinDirection, } from "../../../../../../../imports/schematicModels";
import { getNormalizedManufacturerName } from "../../../../../../../part";
import { KiCadHelpers } from "../../../KiCadHelpers";
/**
 * Imports an Kicad Symbol File and creates a Flux part document.
 *
 * Supoorts EESchema-LIBRARY File Version 5 and higher
 */
export class KiCadModernSymbolParser {
    textFileContent;
    kiCadHelpers;
    constructor(textFileContent) {
        this.textFileContent = textFileContent;
        this.kiCadHelpers = new KiCadHelpers("v5");
    }
    // .kicad_sym Spec https://dev-docs.kicad.org/en/file-formats/sexpr-symbol-lib/
    static isModernSchematic(content) {
        return content.includes("kicad_symbol_lib");
    }
    parse() {
        const parts = this.parseKiCadPartData(this.textFileContent);
        return parts.map((p) => this.mapPartData(p));
    }
    // S-expr Spec for Symbols https://dev-docs.kicad.org/en/file-formats/sexpr-intro/index.html#_symbols
    parseKiCadPartData(textFileContent) {
        const parsedFile = kicadParser.symbol(textFileContent).kicad_symbol_lib;
        const symbols = parsedFile.symbol || [];
        return this.normaliseAliases(symbols);
    }
    normaliseAliases(denormalizedSymbols) {
        const symbolAliasMap = denormalizedSymbols.reduce((map, symbol) => {
            // If alias then keep track of it
            if ("extends" in symbol) {
                const aliases = map.alias[symbol.extends];
                if (!aliases) {
                    map.alias[symbol.extends] = [symbol.id];
                }
                else {
                    aliases.push(symbol.id);
                }
                return map;
            }
            map.symbols[symbol.id] = symbol;
            return map;
        }, { symbols: {}, alias: {} });
        // Assign aliases to primary symbols
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.entries(symbolAliasMap.alias).forEach(([key, value]) => {
            if (symbolAliasMap.symbols[key]) {
                const symbol = symbolAliasMap.symbols[key];
                symbol.alias = value.sort();
            }
        });
        return Object.values(symbolAliasMap.symbols);
    }
    mapPartData(part) {
        return {
            name: part.id,
            designator: part.properties.find(this.mandatoryProp("Reference"))?.value || "",
            description: this.getDescriptionFromSymbol(part),
            alias: part.alias,
            manufacturer: this.getManufacturerFromSymbol(part),
            datasheetUrl: this.getDatasheetUrl(part),
            symbols: this.getSymbolsFromPart(part),
            mpn: part.properties.find(this.mandatoryProp("Value"))?.value || "",
            lcscDpn: this.getPropertyValueForKey(part.properties, "LCSC Part"),
            productDetailUrl: this.getPropertyValueForKey(part.properties, "Product Details"),
            productImageUrl: this.getPropertyValueForKey(part.properties, "Product Image"),
            packageCode: this.getPackageCodeFromSymbol(part),
            partType: this.getPartTypeFromSymbol(part),
            footprint: this.getPropertyValueForKey(part.properties, "Footprint"),
        };
    }
    getSymbolsFromPart(part) {
        function isCommonToAllSymbols(symbol) {
            // Yes, the kicad format explicitly defines  "0" mid-symbol-name to mean "common to all symbols"!
            // https://dev-docs.kicad.org/en/file-formats/sexpr-intro/index.html#_symbol_unit_identifier
            // This is set by the "Properties -> Common to all units in symbol" checkbox in the KiCad
            // symbol editor.
            return symbol.id.match(/.*_(\d+)_\d+$/)?.[1] === "0";
        }
        if (part.symbol) {
            // Multi-symbol part
            // First get all shapes common to all symbols.
            const commonToAllSymbols = part.symbol
                .filter((s) => isCommonToAllSymbols(s))
                .map((s) => this.getSymbolData(s));
            let separateSymbols = part.symbol.filter((s) => !isCommonToAllSymbols(s));
            if (separateSymbols.length === 0 && part.symbol[0]) {
                // If there are only common-to-all symbols, then the first of those appears to be a concrete symbol.
                // See TSC101BILT.kicad_sym test file for an example.
                separateSymbols = [part.symbol[0]];
                commonToAllSymbols.shift();
            }
            const symbols = [];
            for (const symbol of separateSymbols) {
                const symbolData = this.getSymbolData(symbol);
                symbolData.drawInstructions.unshift(...commonToAllSymbols.flatMap((s) => s.drawInstructions));
                symbols.push(symbolData);
            }
            if (symbols.length < 1)
                throw new Error("Multi-symbol part has zero symbols");
            return symbols;
        }
        else {
            // Simple single-symbol part
            return [this.getSymbolData(part)];
        }
    }
    getDatasheetUrl(symbol) {
        const rawDatasheetUrl = this.getPropertyValueForKey(symbol.properties, "Datasheet");
        return KiCadHelpers.cleanupDatasheetUrl(rawDatasheetUrl);
    }
    mandatoryProp(key) {
        return (property) => property.key === key;
    }
    getSymbolData(symbol) {
        const drawInstructions = [];
        const terminalsData = (symbol.pin ?? []).map((pin) => this.parsePin(pin).terminal);
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        symbol.pin?.forEach((pin) => {
            drawInstructions.push(this.parsePin(pin).pin);
        });
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        symbol.circle?.forEach((circle) => {
            drawInstructions.push(this.parseCircle(circle));
        });
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        symbol.arc?.forEach((arc) => {
            drawInstructions.push(this.parseArc(arc));
        });
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        symbol.polyline?.forEach((polyline) => {
            drawInstructions.push(this.parsePolyline(polyline));
        });
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        symbol.rectangle?.forEach((rectangle) => {
            drawInstructions.push(this.parseRectangle(rectangle));
        });
        return { name: symbol.id, drawInstructions, terminalsData };
    }
    // Converts an arc from a representation with start, end and midpoint on the arc line,
    parseArc(entry) {
        const kicadArc = {
            start: { x: entry.start.x, y: entry.start.y },
            end: { x: entry.end.x, y: entry.end.y },
            mid: { x: entry.mid.x, y: entry.mid.y },
            stroke: entry.stroke,
            fill: entry.fill,
        };
        return this.convertToArcWithOrigin(kicadArc);
    }
    // to an arc with an origin, radius, start and end
    convertToArcWithOrigin(arc) {
        const { start, end, mid } = arc;
        const center = KiCadHelpers.calculateCenterPointOfArc(start, mid, end);
        const posX = this.kiCadHelpers.fromKiCadLinearUnits(center.x);
        const posY = this.kiCadHelpers.fromKiCadLinearUnits(center.y);
        const radius = this.kiCadHelpers.fromKiCadLinearUnits(this.getCartesianDistance({
            x: center.x,
            y: center.y,
        }, start));
        const startX = this.kiCadHelpers.fromKiCadLinearUnits(start.x);
        const startY = this.kiCadHelpers.fromKiCadLinearUnits(start.y);
        const endX = this.kiCadHelpers.fromKiCadLinearUnits(end.x);
        const endY = this.kiCadHelpers.fromKiCadLinearUnits(end.y);
        const thickness = Math.max(1, this.kiCadHelpers.fromKiCadLinearUnits(Number(arc.stroke.width)));
        // Get the angle of the start and end points relative to the center point
        const startAngle = Math.round(radiansToDegrees(Math.atan2(start.y - center.y, start.x - center.x)));
        const endAngle = Math.round(radiansToDegrees(Math.atan2(end.y - center.y, end.x - center.x)));
        return {
            type: DrawRecordTypes.arcWithOrigin,
            posX,
            posY,
            radius,
            startAngle,
            endAngle,
            startX,
            startY,
            endX,
            endY,
            thickness,
        };
    }
    getCartesianDistance(a, b) {
        return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
    }
    parseCircle(entry) {
        return {
            type: DrawRecordTypes.circle,
            posX: this.kiCadHelpers.fromKiCadLinearUnits(entry.center.x),
            posY: this.kiCadHelpers.fromKiCadLinearUnits(entry.center.y),
            radius: this.kiCadHelpers.fromKiCadLinearUnits(entry.radius),
            thickness: Math.max(1, this.kiCadHelpers.fromKiCadLinearUnits(Number(entry.stroke.width))),
        };
    }
    parsePolyline(entry) {
        return {
            type: DrawRecordTypes.polyline,
            points: entry.pts.map(({ x, y }) => ({
                x: this.kiCadHelpers.fromKiCadLinearUnits(x),
                y: this.kiCadHelpers.fromKiCadLinearUnits(y),
            })),
            pointCount: entry.pts.length,
            thickness: Math.max(1, this.kiCadHelpers.fromKiCadLinearUnits(Number(entry.stroke.width))),
        };
    }
    parseRectangle(entry) {
        return {
            type: DrawRecordTypes.rectangle,
            startX: this.kiCadHelpers.fromKiCadLinearUnits(entry.start.x),
            startY: this.kiCadHelpers.fromKiCadLinearUnits(entry.start.y),
            endX: this.kiCadHelpers.fromKiCadLinearUnits(entry.end.x),
            endY: this.kiCadHelpers.fromKiCadLinearUnits(entry.end.y),
            thickness: Math.max(1, this.kiCadHelpers.fromKiCadLinearUnits(Number(entry.stroke.width))),
        };
    }
    angleToPinDirection(angle) {
        if (angle >= 45 && angle < 135) {
            return PinDirection.up;
        }
        else if (angle >= 135 && angle < 225) {
            return PinDirection.left;
        }
        else if (angle >= 225 && angle < 315) {
            return PinDirection.down;
        }
        else {
            return PinDirection.right;
        }
    }
    mapKiCadPinTypeToFlux(pinType) {
        switch (pinType) {
            case "input":
                return "Input";
            case "output":
                return "Output";
            case "bidirectional":
                return "Bi-Directional";
            case "tri_state":
                return "Tri-State";
            case "passive":
                return "Passive";
            case "free":
                return "Not Connected";
            case "unspecified":
                return "Unspecified";
            case "power_in":
                return "Power In";
            case "power_out":
                return "Power Out";
            case "open_collector":
                return "Open Collector";
            case "open_emitter":
                return "Open Emitter";
            case "no_connect":
                return "Not Connected";
            default:
                throw new Error("Unknown pin type: " + pinType);
        }
    }
    parsePin(entry) {
        /* eslint-disable id-blacklist */
        const { pin_name, pin_number, at, length } = entry;
        const pinDirection = this.angleToPinDirection(at.angle);
        const terminalType = this.mapKiCadPinTypeToFlux(entry.pin_electrical_type);
        // Convert KiCad pin direction to standardized terminal direction and get position modifiers
        const terminalDirection = this.pinDirectionToTerminalDirection(pinDirection);
        const positionModifiers = getTerminalPositionModifiers(terminalDirection);
        const terminal = {
            name: pin_name.value,
            number: pin_number.value,
            diagramPosition: {
                x: this.kiCadHelpers.spreadXTerminalPosition(this.kiCadHelpers.fromKiCadLinearUnits(at.x)),
                y: this.kiCadHelpers.spreadYTerminalPosition(this.kiCadHelpers.fromKiCadLinearUnits(at.y)),
                flip: positionModifiers.flip,
                ...(positionModifiers.orientation !== undefined && { orientation: positionModifiers.orientation }),
            },
            symbolPosition: {
                // These values are rounded to keep in line with the legacy
                // KiCad Symbol parser
                x: this.kiCadHelpers.fromKiCadLinearUnits(at.x),
                y: this.kiCadHelpers.fromKiCadLinearUnits(at.y),
            },
            type: terminalType,
        };
        const pin = {
            type: DrawRecordTypes.pin,
            pinName: pin_name.value,
            pinNumber: pin_number.value,
            posX: this.kiCadHelpers.fromKiCadLinearUnits(at.x),
            posY: this.kiCadHelpers.fromKiCadLinearUnits(at.y),
            pinLength: this.kiCadHelpers.fromKiCadLinearUnits(length),
            pinDirection,
            flip: positionModifiers.flip,
        };
        /* eslint-enable id-blacklist */
        return { pin, terminal };
    }
    /**
     * Converts KiCad's PinDirection enum to the standardized TerminalDirection type.
     * This allows using the centralized getTerminalPositionModifiers utility.
     */
    pinDirectionToTerminalDirection(pinDirection) {
        switch (pinDirection) {
            case PinDirection.left:
                return "left";
            case PinDirection.right:
                return "right";
            case PinDirection.up:
                return "up";
            case PinDirection.down:
                return "down";
        }
    }
    getDescriptionFromSymbol(symbol) {
        return symbol.properties
            .filter((property) => !["Reference", "Value", "Datasheet", "Footprint"].includes(property.key) && property.value)
            .map((p) => p.value?.trim())
            .filter((line) => line)
            .join("\n");
    }
    getPackageCodeFromSymbol(symbol) {
        const rawFootprint = symbol.properties.find((property) => property.key === "Footprint")?.value;
        return KiCadHelpers.getPackageCodeFromFootprint(rawFootprint);
    }
    getPartTypeFromSymbol(symbol) {
        const nativePartType = this.getPropertyValueForKey(symbol.properties, "Part Type");
        if (nativePartType) {
            return KiCadHelpers.normalizePartType(nativePartType);
        }
        else {
            // fallback to extract from footprint which works in some cases
            const rawFootprint = symbol.properties.find((property) => property.key === "Footprint")?.value;
            const partTypeFromFootprint = KiCadHelpers.getPartTypeFromFootprint(rawFootprint);
            return KiCadHelpers.normalizePartType(partTypeFromFootprint);
        }
    }
    getManufacturerFromSymbol(symbol) {
        for (const property of symbol.properties) {
            const manufacturerName = getNormalizedManufacturerName(property.value);
            if (manufacturerName) {
                return manufacturerName;
            }
        }
        return;
    }
    getPropertyValueForKey(properties, key) {
        return properties.find((property) => property.key === key)?.value;
    }
}
//# sourceMappingURL=KiCadModernSymbolParser.js.map